import type { SuccessGamblingProps } from '@/components/success-gambling/success-gambling.types';
import { useRef } from 'react';
import { useBoolean } from 'usehooks-ts';
import Dialog from '@/components/dialog';
import Copy from '@/components/success-gambling/components/copy';
import CopyLines from '@/components/success-gambling/components/lines';
import Link from '@/components/success-gambling/components/link';
import Page from '@/components/success-gambling/components/page';
import ScrollTo from '@/components/success-gambling/components/scroll-to';
import Section from '@/components/success-gambling/components/section';
import Text from '@/components/success-gambling/components/text';
import { SuccessGamblingProvider, useSuccessGamblingContext } from '@/components/success-gambling/context';
import { getCompanyNames } from '@/components/success-gambling/utils/functions';
import { EMAILS } from '@/components/success-gambling/utils/constants';
import '@/components/success-gambling/success-gambling.css';

function SuccessGamblingPage() {
  const { company, answers } = useSuccessGamblingContext();
  const alternativeMethodsRef = useRef<HTMLDivElement>(null);
  const { value, setFalse, setTrue } = useBoolean(false);

  const scrollToTemplates = () => {
    if (alternativeMethodsRef.current) {
      const { top, left } = alternativeMethodsRef.current.getBoundingClientRect();
      window.scrollTo({
        top: Math.round(top - 12),
        left: left,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Page title='Es sieht gut aus,&shy; wir können Ihnen weiterhelfen!'>
      <Section step='1' title='Datenschutzauskunft (DSA) anfordern'>
        <Text>
          Um die genaue Höhe Ihres Anspruchs zu ermitteln, benötigen wir eine Datenschutzauskunft (<strong>DSA</strong>)
          vom jeweiligen Wettanbieter. Diese enthält die detaillierte Übersicht Ihrer Wettverluste und muss{' '}
          <strong>direkt beim Anbieter</strong> angefordert werden.
        </Text>
        <Text>
          {getCompanyNames(answers).length ? (
            <div>
              <span>
                Nutzen Sie den unteren Button, um automatisch eine <strong>vorausgefüllte E-Mailanfrage</strong> an{' '}
                {getCompanyNames(answers).length < 2 ? 'folgenden' : 'folgende'} Anbieter zu erstellen:{' '}
                <strong>{getCompanyNames(answers).join(', ')}</strong>.
              </span>
              {company.isNoNameCompanyCase && (
                <span>
                  &nbsp;Hier finden Sie auch die&nbsp;
                  <span
                    role='button'
                    tabIndex={0}
                    aria-label='Open list of emails'
                    className='success-gambling-button'
                    onClick={() => setTrue()}
                  >
                    E-Mail Adressen anderer Anbieter
                  </span>
                  .
                </span>
              )}
            </div>
          ) : (
            <div>
              {company.isNoNameCompanyCase && (
                <span>
                  Da Sie keinen Anbieter ausgewählt haben, finden Sie hier die&nbsp;
                  <span
                    role='button'
                    tabIndex={0}
                    aria-label='Open list of emails'
                    className='success-gambling-button'
                    onClick={() => setTrue()}
                  >
                    E-Mail-Adressen aller Anbieter
                  </span>
                  .
                </span>
              )}
            </div>
          )}
        </Text>
        <Link />
        <ScrollTo onClick={scrollToTemplates} />
        <Dialog on={value} onClose={() => setFalse()}>
          <div className='success-gambling-casinos'>
            {Object.entries(EMAILS).map(([name, email]) => (
              <div key={name} className='success-gambling-casino'>
                <div className='success-gambling-casino__name'>{name}</div>
                <Copy text={email} />
              </div>
            ))}
          </div>
        </Dialog>
      </Section>
      <Section step='2' title='Prüfen Sie regelmäßig Ihr E-Mail-Postfach'>
        <Text>
          Sie erhalten Ihre DSA direkt vom Anbieter per E-Mail. Häufig lassen sie sich Zeit (bis zu 30 Tagen) - bleiben
          Sie dran!
        </Text>
      </Section>
      <Section step='3' noLine title='Übermitteln Sie uns die DSA'>
        <Text>
          Sobald Sie die DSA vom Anbieter erhalten haben, senden Sie uns diese bitte zu. Die Anleitung haben wir Ihnen
          bereits per E-Mail geschickt.
        </Text>
      </Section>
      <Section ref={alternativeMethodsRef} title='Alternative Methode'>
        <Text>
          Funktioniert der obere Button für Sie nicht? Kopieren Sie einfach Empfänger, Betreff und Nachricht in Ihre
          Mail-App und senden Sie die E-Mail auf diesem Weg.
        </Text>
        <CopyLines />
      </Section>
    </Page>
  );
}

// TODO: Maybe find more elegant solution with HOC like withContext

export default function SuccessGambling(props: SuccessGamblingProps) {
  const { answers = {}, questions = [], fullName = '', email = '' } = props;

  return (
    <SuccessGamblingProvider answers={answers} questions={questions} fullName={fullName} email={email}>
      <SuccessGamblingPage />
    </SuccessGamblingProvider>
  );
}
